import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import type {
  CancellationToken,
  ChartResponse,
  FailedMeasure,
  QueryLoggingItem,
  RunChartReportRequest,
} from "../../../../../../../../api/types/svTypes.ts";
import { useBiCommonDataContext } from "../../../../contexts/BiCommonDataContext.ts";

type CancellableRequest = {
  cancel: () => void;
};

interface Props {
  requestConfig: RunChartReportRequest;
  runReport: (
    dataSetId: string,
    body: RunChartReportRequest,
    done: (data: ApiResponse<ChartResponse>) => void,
    error: (reason: string | undefined) => void
  ) => CancellationToken;
  updateDevTools?: (loggingItems: QueryLoggingItem[] | undefined) => void;
  setEtag?: (etag: string | undefined, cacheValid: boolean | undefined) => void;
}

export default function useDataLoadingViewer({ requestConfig, runReport, updateDevTools, setEtag }: Props) {
  const { dataSetId } = useBiCommonDataContext();

  const [cancellationToken, setCancellationToken] = useState<CancellableRequest | undefined>(undefined);

  const [calculating, setCalculating] = useState(false);
  const [data, setData] = useState<ChartResponse>();
  const [error, setError] = useState<string>();
  const [failedMeasures, setFailedMeasures] = useState<FailedMeasure[]>([]);
  const [initialized, setInitialized] = useState(false);

  const cancellationTokenRef = useRef<CancellableRequest | undefined>(cancellationToken);
  const buildConfigRef = useRef(requestConfig);
  useEffect(() => {
    cancellationTokenRef.current = cancellationToken;
  }, [cancellationToken]);
  useEffect(() => {
    buildConfigRef.current = requestConfig;
  }, [requestConfig]);

  useEffect(() => {
    return () => {
      cancellationTokenRef.current?.cancel();
    };
  }, []);

  const onCalculated = useCallback((response: ApiResponse<ChartResponse>) => {
    if (response.success) {
      if (response.data.errorCode) {
        setError(response.data.errorCode);
      } else {
        setError(undefined);
        setData(response.data);
        updateDevTools?.(response.data.loggingItems);
        setEtag?.(response.data.etag, undefined);
        setFailedMeasures(response.data.failedMeasures);
      }
    } else {
      setError(response.error?.message);
    }
    setCalculating(false);
    setInitialized(true);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  const onCalculationError = useCallback((reason: string | undefined) => {
    if (reason === "canceled") {
      setError(undefined);
    } else {
      setError(reason || "An error occurred while calculating report data. Please try again.");
    }
    setCalculating(false);
    setInitialized(true);
  }, []);

  const calculate = useCallback(
    () => {
      setCalculating(true);
      cancellationTokenRef.current?.cancel();
      setEtag?.(undefined, undefined);
      setCancellationToken(runReport(dataSetId, buildConfigRef.current, onCalculated, onCalculationError));
    },
    // eslint-disable-next-line  @eslint-react/exhaustive-deps
    [dataSetId]
  );

  return { data, calculating, error, calculate, failedMeasures, initialized };
}
